import { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../../../hooks/useAuth';
import { supabase } from '../../../lib/supabase';
import { mockTeamMembers, mockSettings, TeamMember } from '../../../mocks/step6Data';

const CURRENT_DATE = new Date('2026-03-25');

interface SupabaseMember {
  id: string; user_id: string; name: string; phone: string; status: string;
  step1_completed: boolean; last_contact: string; note: string; join_date: string; parent_id: string | null;
}

interface SupabaseGoal {
  id: string; user_id: string; goal_type: string; target_value: number; current_value: number; updated_at: string;
}

const MOTIVATIONAL_QUOTES = [
  { text: 'Büyük sonuçlar küçük, tutarlı adımların birikmesiyle gelir.', author: 'STEP6 Sistemi' },
  { text: 'Ekibini büyütürken aynı zamanda kendini büyütürsün.', author: 'Liderlik İlkesi' },
  { text: 'İlk 4 kişi en önemlisi. Onları iyi seç, iyi destekle.', author: 'STEP6 Felsefesi' },
  { text: 'Pasif gelir sabır ister. Her gün bir adım, her ay bir fark.', author: 'Network Prensibi' },
  { text: 'Başkasını büyütmek en hızlı büyüme yoludur.', author: 'Ekip Dinamiği' },
];

function mapSupabaseMembers(data: SupabaseMember[]): TeamMember[] {
  const l1Members = data.filter(m => !m.parent_id);
  const l2Members = data.filter(m => m.parent_id);
  return l1Members.map(l1 => ({
    id: l1.id, name: l1.name, phone: l1.phone,
    status: l1.status as TeamMember['status'], step1Completed: l1.step1_completed,
    lastContact: l1.last_contact, note: l1.note, joinDate: l1.join_date,
    ownTeam: l2Members.filter(l2 => l2.parent_id === l1.id).map(c => ({
      id: c.id, name: c.name, phone: c.phone,
      status: c.status as TeamMember['status'], step1Completed: c.step1_completed,
      lastContact: c.last_contact, note: c.note, joinDate: c.join_date,
    })),
  }));
}

function computeMonthlyIncome(members: TeamMember[], settings: typeof mockSettings): number {
  return members.length * settings.seviye1Kazanc + members.flatMap(m => m.ownTeam || []).length * settings.seviye2Kazanc;
}

export default function MotivasyonVizyon() {
  const { profile } = useAuth();
  const [members, setMembers] = useState<TeamMember[]>([]);
  const [settings, setSettings] = useState(mockSettings);
  const [incomeGoal, setIncomeGoal] = useState(3000);
  const [teamGoal, setTeamGoal] = useState(20);
  const [editingGoal, setEditingGoal] = useState<'income' | 'team' | null>(null);
  const [tempValue, setTempValue] = useState('');
  const [quoteIdx] = useState(() => CURRENT_DATE.getDate() % MOTIVATIONAL_QUOTES.length);
  const [loading, setLoading] = useState(true);
  const [savingGoal, setSavingGoal] = useState(false);

  // Load everything from Supabase
  useEffect(() => {
    let cancelled = false;

    async function load() {
      setLoading(true);

      if (!profile) {
        setMembers(mockTeamMembers);
        setSettings(mockSettings);
        setIncomeGoal(3000);
        setTeamGoal(20);
        setLoading(false);
        return;
      }

      try {
        // Load team members
        const { data: membersData } = await supabase
          .from('step6_team_members')
          .select('*')
          .eq('user_id', profile.id);

        let loadedMembers = mockTeamMembers;
        if (membersData && membersData.length > 0) {
          loadedMembers = mapSupabaseMembers(membersData as SupabaseMember[]);
        }

        // Load settings
        const { data: settingsData } = await supabase
          .from('step6_settings')
          .select('*')
          .eq('user_id', profile.id)
          .maybeSingle();

        let loadedSettings = mockSettings;
        if (settingsData) {
          loadedSettings = {
            aktiflikBedeli: settingsData.aktiflik_bedeli || mockSettings.aktiflikBedeli,
            kayitPrimi: settingsData.kayit_primi || mockSettings.kayitPrimi,
            seviye1Kazanc: settingsData.seviye1_kazanc || mockSettings.seviye1Kazanc,
            seviye2Kazanc: settingsData.seviye2_kazanc || mockSettings.seviye2Kazanc,
            seviye3Kazanc: settingsData.seviye3_kazanc || mockSettings.seviye3Kazanc,
            seviye4Kazanc: settingsData.seviye4_kazanc || mockSettings.seviye4Kazanc,
            seviye5Kazanc: settingsData.seviye5_kazanc || mockSettings.seviye5Kazanc,
            seviye6Kazanc: settingsData.seviye6_kazanc || mockSettings.seviye6Kazanc,
          };
        }

        if (cancelled) return;
        setMembers(loadedMembers);
        setSettings(loadedSettings);

        // Load goals
        const { data: goalsData } = await supabase
          .from('step6_goals')
          .select('*')
          .eq('user_id', profile.id);

        if (goalsData && goalsData.length > 0) {
          const goals = goalsData as SupabaseGoal[];
          const inc = goals.find(g => g.goal_type === 'income');
          const team = goals.find(g => g.goal_type === 'team');
          if (inc) setIncomeGoal(inc.target_value);
          if (team) setTeamGoal(team.target_value);
        } else {
          // Seed default goals
          const currentInc = computeMonthlyIncome(loadedMembers, loadedSettings);
          const totalTeam = loadedMembers.length + loadedMembers.flatMap(m => m.ownTeam || []).length;
          await supabase.from('step6_goals').insert([
            { user_id: profile.id, goal_type: 'income', target_value: 3000, current_value: currentInc },
            { user_id: profile.id, goal_type: 'team', target_value: 20, current_value: totalTeam },
          ]);
        }
      } catch {
        if (!cancelled) {
          setMembers(mockTeamMembers);
          setSettings(mockSettings);
        }
      }

      if (!cancelled) setLoading(false);
    }

    load();
    return () => { cancelled = true; };
  }, [profile]);

  // Computed values
  const l1 = members;
  const l2 = useMemo(() => members.flatMap(m => m.ownTeam || []), [members]);
  const step1L1 = l1.filter(m => m.step1Completed).length;
  const totalTeam = l1.length + l2.length;
  const currentIncome = useMemo(() => computeMonthlyIncome(members, settings), [members, settings]);
  const fullCapacityIncome = 4 * settings.seviye1Kazanc + 16 * settings.seviye2Kazanc + 64 * settings.seviye3Kazanc;

  const incomeProgress = Math.min(100, Math.round((currentIncome / incomeGoal) * 100));
  const teamProgress = Math.min(100, Math.round((totalTeam / teamGoal) * 100));

  // Projections
  const proj3m = useMemo(() => {
    const l2Size = Math.min(16, l2.length + 6);
    return l1.length * settings.seviye1Kazanc + l2Size * settings.seviye2Kazanc;
  }, [l1.length, l2.length, settings]);
  const proj6m = useMemo(() => {
    const l2Size = Math.min(16, l2.length + 12);
    return l1.length * settings.seviye1Kazanc + l2Size * settings.seviye2Kazanc;
  }, [l1.length, l2.length, settings]);

  const monthsToGoal = useMemo(() => {
    let l2Size = l2.length;
    let months = 0;
    while (months < 36) {
      const inc = l1.length * settings.seviye1Kazanc + l2Size * settings.seviye2Kazanc;
      if (inc >= incomeGoal) break;
      l2Size = Math.min(16, l2Size + 2);
      months++;
    }
    return months;
  }, [incomeGoal, l1.length, l2.length, settings]);

  // Goal editing
  const startGoalEdit = (type: 'income' | 'team') => {
    setEditingGoal(type);
    setTempValue(String(type === 'income' ? incomeGoal : teamGoal));
  };

  const saveGoal = async () => {
    const val = Number(tempValue);
    if (isNaN(val) || val <= 0) return;

    // Optimistic
    if (editingGoal === 'income') setIncomeGoal(val);
    else setTeamGoal(val);
    setEditingGoal(null);

    if (!profile) return;

    setSavingGoal(true);
    await supabase
      .from('step6_goals')
      .update({ target_value: val, updated_at: new Date().toISOString() })
      .eq('user_id', profile.id)
      .eq('goal_type', editingGoal);
    setSavingGoal(false);
  };

  // Achievements
  const achievements = [
    { label: 'İlk Kişiyi Ekledin', icon: 'ri-user-add-line', done: l1.length >= 1, color: '#F5A623', date: '2026-02-15' },
    { label: 'Aktifliğini Tamamladın', icon: 'ri-shield-check-line', done: true, color: '#22C55E', date: '2026-02-01' },
    { label: 'İlk 4 Kişi Tamamlandı', icon: 'ri-team-line', done: l1.length === 4, color: '#38BDF8', date: '2026-03-05' },
    { label: '1. Adım Yarısı Geçildi', icon: 'ri-footprint-line', done: step1L1 >= 2, color: '#A78BFA', date: '2026-03-21' },
    { label: 'L2 Ekip Kuruldu', icon: 'ri-group-line', done: l2.length >= 4, color: '#34D399', date: '2026-03-12' },
    { label: 'Aktifliği Karşıladın', icon: 'ri-money-dollar-circle-line', done: currentIncome >= settings.aktiflikBedeli, color: '#FB923C', date: null },
    { label: 'L1 Tamamı 1. Adımda', icon: 'ri-award-line', done: step1L1 === 4, color: '#F43F5E', date: null },
    { label: '2. Adım Geçişi', icon: 'ri-rocket-2-line', done: l2.length >= 16, color: '#818CF8', date: null },
  ];

  const doneBadges = achievements.filter(a => a.done).length;

  const milestones = [
    {
      label: 'STEP6 Yolculuğu Başladı', detail: 'Sisteme dahil oldun, yolculuk başladı.',
      date: '01 Şubat 2026', status: 'done' as const, icon: 'ri-flag-2-line', color: '#22C55E',
    },
    {
      label: 'İlk 4 Kişilik Ekip', detail: `L1 ekibin tamamlandı — ${l1.length}/4 kişi.`,
      date: '05 Mart 2026', status: 'done' as const, icon: 'ri-team-line', color: '#38BDF8',
    },
    {
      label: 'L1 Ekibinin Tamamı 1. Adımda', detail: `Şu an ${step1L1}/4 kişi tamamladı. ${4 - step1L1} kişi kaldı.`,
      date: null, status: step1L1 === 4 ? 'done' as const : 'active' as const, icon: 'ri-footprint-line', color: '#F5A623',
    },
    {
      label: '2. Adım: 16 Kişilik L2', detail: `L2 ekibinde ${l2.length}/16 kişi var. ${16 - l2.length} kişi kaldı.`,
      date: null, status: l2.length >= 16 ? 'done' as const : (l2.length > 0 ? 'active' as const : 'locked' as const), icon: 'ri-bar-chart-2-fill', color: '#A78BFA',
    },
    {
      label: '3. Adım: 64 Kişilik L3', detail: 'L3 ekibini de oluştur — tam 3 jenerasyon.',
      date: null, status: 'locked' as const, icon: 'ri-bar-chart-fill', color: '#FB923C',
    },
    {
      label: '6. Adım: Tam Network', detail: 'Tüm 6 adımı tamamla — maksimum pasif gelir.',
      date: null, status: 'locked' as const, icon: 'ri-trophy-fill', color: '#F5A623',
    },
  ];

  const quote = MOTIVATIONAL_QUOTES[quoteIdx];

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="flex flex-col items-center gap-4">
          <div className="w-10 h-10 rounded-full border-2 border-amber-400 border-t-transparent animate-spin" />
          <p className="text-sm text-gray-400">Motivasyon verileri yükleniyor...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white">Motivasyon & Vizyon</h1>
          <p className="text-sm text-gray-500 mt-1">Hedeflerini belirle, projeksiyonunu gör, yolculuğunu takip et</p>
        </div>
        {/* Daily quote pill */}
        <div className="rounded-2xl px-4 py-3 max-w-xs" style={{ background: 'rgba(245,166,35,0.07)', border: '1px solid rgba(245,166,35,0.15)' }}>
          <p className="text-xs italic text-amber-200/80 leading-relaxed">&ldquo;{quote.text}&rdquo;</p>
          <p className="text-xs text-amber-400/60 mt-1 font-medium">— {quote.author}</p>
        </div>
      </div>

      {/* Stats row */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {[
          { label: 'Mevcut Aylık Gelir', value: `₺${currentIncome.toLocaleString('tr-TR')}`, icon: 'ri-money-dollar-circle-line', color: 'text-amber-400', bg: 'rgba(245,166,35,0.08)' },
          { label: 'Toplam Ekip', value: `${totalTeam} kişi`, icon: 'ri-group-line', color: 'text-sky-400', bg: 'rgba(56,189,248,0.08)' },
          { label: '1. Adım Tamamlayan', value: `${step1L1}/4`, icon: 'ri-footprint-line', color: 'text-purple-400', bg: 'rgba(168,85,247,0.08)' },
          { label: 'Rozet Kazanıldı', value: `${doneBadges}/${achievements.length}`, icon: 'ri-trophy-line', color: 'text-emerald-400', bg: 'rgba(52,211,153,0.08)' },
        ].map((s, i) => (
          <div key={i} className="rounded-2xl p-4" style={{ background: '#12141E', border: '1px solid rgba(255,255,255,0.06)' }}>
            <div className="w-8 h-8 rounded-xl flex items-center justify-center mb-2" style={{ background: s.bg }}>
              <i className={`${s.icon} text-sm ${s.color}`} />
            </div>
            <div className={`text-xl font-black ${s.color}`}>{s.value}</div>
            <div className="text-xs text-gray-500 mt-0.5">{s.label}</div>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* ── Hedef Belirleme ── */}
        <div className="rounded-2xl p-6" style={{ background: '#12141E', border: '1px solid rgba(255,255,255,0.06)' }}>
          <div className="flex items-center gap-2 mb-5">
            <div className="w-8 h-8 rounded-xl flex items-center justify-center" style={{ background: 'rgba(245,166,35,0.12)' }}>
              <i className="ri-focus-3-line text-amber-400 text-base" />
            </div>
            <div>
              <h2 className="text-sm font-bold text-white">Hedef Belirle</h2>
              <p className="text-xs text-gray-500">Kişisel hedeflerini gir, ilerlemeyi izle</p>
            </div>
          </div>

          {/* Income goal */}
          <div className="mb-5">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-semibold text-gray-300">Aylık Gelir Hedefi</span>
              {editingGoal === 'income' ? (
                <div className="flex items-center gap-2">
                  <span className="text-xs text-gray-500">₺</span>
                  <input type="number" value={tempValue} onChange={e => setTempValue(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && saveGoal()}
                    className="w-24 rounded-lg px-2 py-1 text-xs text-right text-white font-bold outline-none text-sm"
                    style={{ background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(245,166,35,0.4)' }}
                    autoFocus />
                  <button onClick={saveGoal} disabled={savingGoal} className="text-xs text-amber-400 font-semibold cursor-pointer disabled:opacity-50 whitespace-nowrap">
                    {savingGoal ? '...' : 'Kaydet'}
                  </button>
                </div>
              ) : (
                <button onClick={() => startGoalEdit('income')} className="flex items-center gap-1.5 text-xs text-amber-400 font-bold cursor-pointer hover:opacity-80 whitespace-nowrap">
                  ₺{incomeGoal.toLocaleString('tr-TR')} <i className="ri-pencil-line text-xs" />
                </button>
              )}
            </div>
            <div className="h-3 rounded-full overflow-hidden mb-1.5" style={{ background: 'rgba(255,255,255,0.06)' }}>
              <div className="h-full rounded-full transition-all duration-700 relative"
                style={{ width: `${incomeProgress}%`, background: 'linear-gradient(90deg, #F5A623, #FF6B35)' }} />
            </div>
            <div className="flex items-center justify-between">
              <span className="text-xs text-gray-600">₺{currentIncome.toLocaleString('tr-TR')} mevcut</span>
              <span className="text-xs font-bold text-amber-400">%{incomeProgress}</span>
            </div>
            {incomeProgress < 100 && (
              <p className="text-xs text-gray-600 mt-1.5 flex items-center gap-1">
                <i className="ri-time-line text-gray-700 text-xs" />
                Mevcut hızda ~{monthsToGoal === 0 ? 'hedefe ulaştın!' : `${monthsToGoal} ayda hedefe ulaşırsın`}
              </p>
            )}
            {incomeProgress >= 100 && (
              <p className="text-xs text-green-400 mt-1.5 font-semibold">Hedef geliri aştın! Yeni hedef belirle.</p>
            )}
          </div>

          {/* Team size goal */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-semibold text-gray-300">Ekip Büyüklüğü Hedefi</span>
              {editingGoal === 'team' ? (
                <div className="flex items-center gap-2">
                  <input type="number" value={tempValue} onChange={e => setTempValue(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && saveGoal()}
                    className="w-16 rounded-lg px-2 py-1 text-xs text-right text-white font-bold outline-none text-sm"
                    style={{ background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(56,189,248,0.4)' }}
                    autoFocus />
                  <button onClick={saveGoal} disabled={savingGoal} className="text-xs text-sky-400 font-semibold cursor-pointer disabled:opacity-50 whitespace-nowrap">
                    {savingGoal ? '...' : 'Kaydet'}
                  </button>
                </div>
              ) : (
                <button onClick={() => startGoalEdit('team')} className="flex items-center gap-1.5 text-xs text-sky-400 font-bold cursor-pointer hover:opacity-80 whitespace-nowrap">
                  {teamGoal} kişi <i className="ri-pencil-line text-xs" />
                </button>
              )}
            </div>
            <div className="h-3 rounded-full overflow-hidden mb-1.5" style={{ background: 'rgba(255,255,255,0.06)' }}>
              <div className="h-full rounded-full transition-all duration-700"
                style={{ width: `${teamProgress}%`, background: 'linear-gradient(90deg, #38BDF8, #818CF8)' }} />
            </div>
            <div className="flex items-center justify-between">
              <span className="text-xs text-gray-600">{totalTeam} kişi mevcut</span>
              <span className="text-xs font-bold text-sky-400">%{teamProgress}</span>
            </div>
            {teamProgress >= 100 && (
              <p className="text-xs text-green-400 mt-1.5 font-semibold">Ekip hedefine ulaştın!</p>
            )}
          </div>
        </div>

        {/* ── Gelecek Projeksiyonu ── */}
        <div className="rounded-2xl p-6" style={{ background: '#12141E', border: '1px solid rgba(255,255,255,0.06)' }}>
          <div className="flex items-center gap-2 mb-5">
            <div className="w-8 h-8 rounded-xl flex items-center justify-center" style={{ background: 'rgba(52,211,153,0.12)' }}>
              <i className="ri-line-chart-line text-emerald-400 text-base" />
            </div>
            <div>
              <h2 className="text-sm font-bold text-white">Gelecek Projeksiyonu</h2>
              <p className="text-xs text-gray-500">Mevcut büyüme hızına göre tahmin</p>
            </div>
          </div>

          <div className="space-y-3">
            {[
              { label: 'Şu An', income: currentIncome, team: totalTeam, color: '#6B7280', bar: 'rgba(107,114,128,0.4)', accent: 'text-gray-400' },
              { label: '3 Ay Sonra', income: proj3m, team: Math.min(20, totalTeam + 6), color: '#F5A623', bar: '#F5A623', accent: 'text-amber-400' },
              { label: '6 Ay Sonra', income: proj6m, team: Math.min(25, totalTeam + 12), color: '#34D399', bar: '#34D399', accent: 'text-emerald-400' },
              { label: 'Tam Kapasite', income: fullCapacityIncome, team: 4 + 16 + 64, color: '#A78BFA', bar: '#A78BFA', accent: 'text-purple-400' },
            ].map((p, i) => (
              <div key={i} className="rounded-xl p-3" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.05)' }}>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-semibold text-gray-300">{p.label}</span>
                  <div className="flex items-center gap-3">
                    <span className="text-xs text-gray-600">{p.team} kişi</span>
                    <span className={`text-sm font-black ${p.accent}`}>₺{p.income.toLocaleString('tr-TR')}</span>
                  </div>
                </div>
                <div className="h-1.5 rounded-full overflow-hidden" style={{ background: 'rgba(255,255,255,0.05)' }}>
                  <div className="h-full rounded-full transition-all duration-700"
                    style={{ width: `${Math.min(100, Math.round((p.income / fullCapacityIncome) * 100))}%`, background: p.bar }} />
                </div>
              </div>
            ))}
          </div>

          <p className="text-xs text-gray-600 mt-4 flex items-center gap-1.5">
            <i className="ri-information-line text-gray-700" />
            Aylık ~2 L2 üye büyümesi varsayımıyla hesaplanmıştır.
          </p>
        </div>
      </div>

      {/* ── Milestone Zaman Çizelgesi ── */}
      <div className="rounded-2xl p-6" style={{ background: '#12141E', border: '1px solid rgba(255,255,255,0.06)' }}>
        <div className="flex items-center gap-2 mb-6">
          <div className="w-8 h-8 rounded-xl flex items-center justify-center" style={{ background: 'rgba(245,166,35,0.12)' }}>
            <i className="ri-map-pin-time-line text-amber-400 text-base" />
          </div>
          <div>
            <h2 className="text-sm font-bold text-white">Milestone Yol Haritası</h2>
            <p className="text-xs text-gray-500">Geçmişten geleceğe STEP6 yolculuğun</p>
          </div>
        </div>

        <div className="relative">
          {/* vertical line */}
          <div className="absolute left-5 top-4 bottom-4 w-0.5" style={{ background: 'rgba(255,255,255,0.06)' }} />

          <div className="space-y-0">
            {milestones.map((m, i) => {
              const isDone = m.status === 'done';
              const isActive = m.status === 'active';
              const isLocked = m.status === 'locked';
              return (
                <div key={i} className="flex items-start gap-4 pb-6 last:pb-0 relative">
                  {/* Node */}
                  <div className="relative z-10 flex-shrink-0">
                    <div className="w-10 h-10 rounded-full flex items-center justify-center transition-all"
                      style={{
                        background: isDone ? `${m.color}22` : isActive ? `${m.color}15` : 'rgba(255,255,255,0.04)',
                        border: `2px solid ${isDone ? m.color : isActive ? m.color : 'rgba(255,255,255,0.1)'}`,
                      }}>
                      {isDone ? (
                        <i className="ri-check-line text-sm" style={{ color: m.color }} />
                      ) : isActive ? (
                        <i className={`${m.icon} text-sm`} style={{ color: m.color }} />
                      ) : (
                        <i className="ri-lock-line text-gray-600 text-sm" />
                      )}
                    </div>
                    {isActive && (
                      <span className="absolute -top-1 -right-1 w-3 h-3 rounded-full border-2 border-gray-900 animate-pulse"
                        style={{ background: m.color }} />
                    )}
                  </div>

                  {/* Content */}
                  <div className="flex-1 pt-1.5 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className={`text-sm font-bold ${isDone ? 'text-white' : isActive ? 'text-white' : 'text-gray-600'}`}>
                        {m.label}
                      </span>
                      {isDone && m.date && (
                        <span className="text-xs px-2 py-0.5 rounded-full font-medium text-green-400" style={{ background: 'rgba(34,197,94,0.1)' }}>
                          ✓ {m.date}
                        </span>
                      )}
                      {isActive && (
                        <span className="text-xs px-2 py-0.5 rounded-full font-medium animate-pulse" style={{ background: `${m.color}20`, color: m.color }}>
                          Devam ediyor
                        </span>
                      )}
                      {isLocked && (
                        <span className="text-xs px-2 py-0.5 rounded-full text-gray-600" style={{ background: 'rgba(255,255,255,0.05)' }}>
                          Kilitli
                        </span>
                      )}
                    </div>
                    <p className={`text-xs mt-0.5 ${isDone ? 'text-gray-500' : isActive ? 'text-gray-400' : 'text-gray-700'}`}>
                      {m.detail}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* ── Rozet Koleksiyonu ── */}
      <div className="rounded-2xl p-6" style={{ background: '#12141E', border: '1px solid rgba(255,255,255,0.06)' }}>
        <div className="flex items-center justify-between mb-5">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-xl flex items-center justify-center" style={{ background: 'rgba(245,166,35,0.12)' }}>
              <i className="ri-trophy-line text-amber-400 text-base" />
            </div>
            <div>
              <h2 className="text-sm font-bold text-white">Rozet Koleksiyonu</h2>
              <p className="text-xs text-gray-500">{doneBadges}/{achievements.length} rozet kazanıldı</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <div className="h-2 w-28 rounded-full overflow-hidden" style={{ background: 'rgba(255,255,255,0.06)' }}>
              <div className="h-full rounded-full transition-all duration-700"
                style={{ width: `${Math.round((doneBadges / achievements.length) * 100)}%`, background: 'linear-gradient(90deg, #F5A623, #FF6B35)' }} />
            </div>
            <span className="text-xs font-bold text-amber-400">%{Math.round((doneBadges / achievements.length) * 100)}</span>
          </div>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {achievements.map((a, i) => (
            <div key={i} className="flex flex-col items-center gap-2 p-4 rounded-xl text-center transition-all"
              style={{
                background: a.done ? `${a.color}10` : 'rgba(255,255,255,0.02)',
                border: `1px solid ${a.done ? `${a.color}30` : 'rgba(255,255,255,0.05)'}`,
                opacity: a.done ? 1 : 0.4,
              }}>
              <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
                style={{ background: a.done ? `${a.color}20` : 'rgba(255,255,255,0.04)' }}>
                <i className={`${a.icon} text-base`} style={{ color: a.done ? a.color : '#4B5563' }} />
              </div>
              <p className={`text-xs font-medium leading-tight ${a.done ? 'text-white' : 'text-gray-600'}`}>
                {a.label}
              </p>
              {a.done && a.date && <p className="text-xs font-medium" style={{ color: a.color }}>✓ {a.date.slice(5)}</p>}
              {a.done && !a.date && <p className="text-xs text-green-400 font-medium">✓ Kazanıldı</p>}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}